import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
    Calendar as CalendarIcon, 
    Users, 
    Sun, 
    Moon, 
    Star,
    RefreshCw,
    AlertCircle,
    Building2,
    Briefcase
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';

// Configuration des restaurants avec Zenchef
const ZENCHEF_RESTAURANTS = [
    { id: 'all', name: 'Vue Groupe', logo: '/logos/all4you-groupe.png', slug: 'all' },
    { id: 'ma-biche', name: 'Ma biche sur le toit', logo: '/logos/ma-biche-sur-le-toit.png', slug: 'Ma biche sur le toit' },
    { id: 'compagnie-francaise', name: 'Compagnie Française', logo: '/logos/compagnie-francaise.png', slug: 'Compagnie Française' },
    { id: 'compagnie-busca', name: 'Compagnie Busca', logo: '/logos/compagnie-busca.png', slug: 'Compagnie Busca' },
    { id: 'jacques-balma', name: 'Jacques Balma', logo: '/logos/jacques-balma.png', slug: 'Jacques Balma', subtitle: 'Balma' },
    { id: 'cafe-chouchou', name: 'Café Chouchou', logo: '/logos/cafe-chouchou.png', slug: 'Café Chouchou' },
    { id: 'sous-les-pins', name: 'Sous les pins', logo: '/logos/sous-les-pins.png', slug: 'Sous les pins' },
    { id: 'coup-de-bambou', name: 'Coup de Bambou', logo: '/logos/coup-de-bambou.png', slug: 'Coup de bambou' },
];

// Fonction pour formater les nombres
const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('fr-FR').format(num);
};

// Composant pour le sélecteur de restaurant avec logos (même style que Ventes Générales)
const RestaurantSelector = ({ selected, onSelect }) => {
    return (
        <div 
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3"
            data-testid="restaurant-selector-info"
        >
            {ZENCHEF_RESTAURANTS.map((resto) => (
                <button
                    key={resto.id}
                    onClick={() => onSelect(resto)}
                    className={cn(
                        "relative h-24 md:h-28 rounded-xl overflow-hidden cursor-pointer transition-all duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                        "hover:scale-105 hover:shadow-lg",
                        selected.id === resto.id 
                            ? "ring-2 ring-primary ring-offset-2 shadow-lg" 
                            : "ring-1 ring-border"
                    )}
                    data-testid={`restaurant-tab-${resto.id}`}
                >
                    {/* Fond noir */}
                    <div className="absolute inset-0 bg-black" />
                    
                    {/* Logo centré */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                        <img
                            src={resto.logo}
                            alt={resto.name}
                            className={cn(
                                "max-w-full object-contain",
                                resto.subtitle ? "max-h-[65%]" : "max-h-[80%]"
                            )}
                        />
                        {resto.subtitle && (
                            <span className="text-white text-xs font-medium mt-1 tracking-wider">
                                {resto.subtitle}
                            </span>
                        )}
                    </div>
                </button>
            ))}
        </div>
    );
};

// Composant tooltip VIP pour afficher les détails au survol
const VipTooltipContent = ({ vips, period }) => {
    const filteredVips = vips.filter(v => v.meal_period === period);
    
    if (filteredVips.length === 0) {
        return <p className="text-xs text-muted-foreground">Aucun VIP</p>;
    }
    
    return (
        <div className="space-y-2 min-w-[180px]">
            <p className="font-semibold text-sm border-b pb-1 mb-2">
                {filteredVips.length} Client{filteredVips.length > 1 ? 's' : ''} VIP - {period === 'midi' ? 'Midi' : 'Soir'}
            </p>
            {filteredVips.map((vip, idx) => (
                <div key={idx} className="flex justify-between items-center gap-4 py-1">
                    <div className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-amber-500" />
                        <span className="font-medium text-sm">{vip.name || 'Client VIP'}</span>
                    </div>
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded whitespace-nowrap">
                        {vip.visit_count > 0 ? `${vip.visit_count} visites` : 'VIP'}
                    </span>
                </div>
            ))}
        </div>
    );
};

// Composant carte de réservation par restaurant avec VIP tooltip
const ReservationCard = ({ restaurant, covers_midi, covers_soir, covers_total, reservations_midi, reservations_soir, vips = [] }) => {
    const vipsMidi = vips.filter(v => v.meal_period === 'midi');
    const vipsSoir = vips.filter(v => v.meal_period === 'soir');
    
    return (
        <Card className="hover:shadow-md transition-all duration-200">
            <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">{restaurant}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {/* Total */}
                    <div className="flex justify-between items-center pb-2 border-b">
                        <span className="text-sm text-muted-foreground">Total couverts</span>
                        <span className="text-2xl font-bold text-primary">{formatNumber(covers_total)}</span>
                    </div>
                    
                    {/* Midi avec tooltip VIP */}
                    <TooltipProvider delayDuration={100}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="flex justify-between items-center cursor-help hover:bg-muted/50 rounded px-1 -mx-1 py-1">
                                    <div className="flex items-center gap-2">
                                        <Sun className="w-4 h-4 text-yellow-500" />
                                        <span className="text-sm text-muted-foreground">Midi</span>
                                        {vipsMidi.length > 0 && (
                                            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                                <Star className="w-3 h-3" />
                                                {vipsMidi.length}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <span className="font-semibold">{formatNumber(covers_midi)}</span>
                                        <span className="text-xs text-muted-foreground ml-1">cvs</span>
                                        <span className="text-xs text-muted-foreground ml-2">({reservations_midi || 0} résa)</span>
                                    </div>
                                </div>
                            </TooltipTrigger>
                            {vipsMidi.length > 0 && (
                                <TooltipContent side="top" className="bg-white dark:bg-gray-900 border shadow-xl p-3 z-50">
                                    <VipTooltipContent vips={vips} period="midi" />
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </TooltipProvider>
                    
                    {/* Soir avec tooltip VIP */}
                    <TooltipProvider delayDuration={100}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="flex justify-between items-center cursor-help hover:bg-muted/50 rounded px-1 -mx-1 py-1">
                                    <div className="flex items-center gap-2">
                                        <Moon className="w-4 h-4 text-indigo-500" />
                                        <span className="text-sm text-muted-foreground">Soir</span>
                                        {vipsSoir.length > 0 && (
                                            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                                <Star className="w-3 h-3" />
                                                {vipsSoir.length}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <span className="font-semibold">{formatNumber(covers_soir)}</span>
                                        <span className="text-xs text-muted-foreground ml-1">cvs</span>
                                        <span className="text-xs text-muted-foreground ml-2">({reservations_soir || 0} résa)</span>
                                    </div>
                                </div>
                            </TooltipTrigger>
                            {vipsSoir.length > 0 && (
                                <TooltipContent side="top" className="bg-white dark:bg-gray-900 border shadow-xl p-3 z-50">
                                    <VipTooltipContent vips={vips} period="soir" />
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </CardContent>
        </Card>
    );
};

// Composant carte VIP
const VipCard = ({ vip }) => {
    return (
        <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-full">
                    <Star className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                    <p className="font-semibold text-sm">{vip.name || 'Client VIP'}</p>
                    <p className="text-xs text-muted-foreground">
                        {vip.restaurant} - {vip.time} ({vip.meal_period})
                    </p>
                </div>
            </div>
            <div className="text-right">
                <p className="text-sm font-medium">{vip.party_size} couverts</p>
                {vip.visit_count > 0 && (
                    <p className="text-xs text-amber-600">{vip.visit_count} visites</p>
                )}
            </div>
        </div>
    );
};

export default function InformationsPage() {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedRestaurant, setSelectedRestaurant] = useState(ZENCHEF_RESTAURANTS[0]); // Vue Groupe par défaut
    const [reservationsData, setReservationsData] = useState(null);
    const [vipData, setVipData] = useState(null);
    const [btobData, setBtobData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        
        try {
            // Récupérer les réservations, VIP et BtoB en parallèle
            const [reservationsRes, vipRes, btobRes] = await Promise.all([
                api.get(`/reservations/summary?date_str=${dateStr}`),
                api.get(`/reservations/vip?date_str=${dateStr}`),
                api.get(`/reservations/btob?date_str=${dateStr}`).catch(() => ({ data: { groups: [] } }))
            ]);
            
            setReservationsData(reservationsRes.data);
            setVipData(vipRes.data);
            setBtobData(btobRes.data);
        } catch (err) {
            console.error('Erreur chargement données:', err);
            setError('Impossible de charger les données de réservation');
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDateSelect = (date) => {
        if (date) {
            setSelectedDate(date);
        }
    };

    // Filtrer les données selon le restaurant sélectionné
    const getFilteredData = () => {
        if (!reservationsData?.restaurants) return [];
        
        // Exclure les restaurants non mappés (commençant par "Restaurant #")
        const validRestaurants = reservationsData.restaurants.filter(
            r => !r.restaurant.startsWith('Restaurant #')
        );
        
        if (selectedRestaurant.id === 'all') {
            return validRestaurants;
        }
        
        return validRestaurants.filter(
            r => r.restaurant === selectedRestaurant.slug
        );
    };

    // Calculer les totaux filtrés
    const getFilteredTotals = () => {
        const filtered = getFilteredData();
        return {
            total_covers: filtered.reduce((sum, r) => sum + (r.covers_total || 0), 0),
            total_covers_midi: filtered.reduce((sum, r) => sum + (r.covers_midi || 0), 0),
            total_covers_soir: filtered.reduce((sum, r) => sum + (r.covers_soir || 0), 0),
            total_reservations: filtered.reduce((sum, r) => sum + (r.reservations_total || 0), 0),
        };
    };

    // Filtrer les VIP selon le restaurant
    const getFilteredVips = () => {
        if (!vipData?.vips) return [];
        
        if (selectedRestaurant.id === 'all') {
            return vipData.vips;
        }
        
        return vipData.vips.filter(v => v.restaurant === selectedRestaurant.slug);
    };

    // Obtenir les VIPs par restaurant pour les cartes
    const getVipsForRestaurant = (restaurantName) => {
        if (!vipData?.vips) return [];
        return vipData.vips.filter(v => v.restaurant === restaurantName);
    };

    // Filtrer les groupes BtoB selon le restaurant
    const getFilteredBtob = () => {
        if (!btobData?.groups) return [];
        
        if (selectedRestaurant.id === 'all') {
            return btobData.groups;
        }
        
        return btobData.groups.filter(g => 
            g.restaurant === selectedRestaurant.slug || 
            g.restaurant === selectedRestaurant.name
        );
    };

    const filteredData = getFilteredData();
    const filteredTotals = getFilteredTotals();
    const filteredVips = getFilteredVips();
    const filteredBtob = getFilteredBtob();

    return (
        <div className="flex h-screen bg-background">
            {/* Sidebar */}
            <Sidebar />
            
            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header />
                
                <main className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6 max-w-7xl mx-auto">
                        {/* Header avec titre et contrôles */}
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold">Informations Générales</h1>
                                <p className="text-muted-foreground">
                                    Réservations et clients VIP - {selectedRestaurant.name}
                                </p>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                {/* Sélecteur de date */}
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                "justify-start text-left font-normal min-w-[200px]",
                                                !selectedDate && "text-muted-foreground"
                                            )}
                                            data-testid="date-selector-info"
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {selectedDate ? format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="end">
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={handleDateSelect}
                                            locale={fr}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                                
                                {/* Bouton refresh */}
                                <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={fetchData}
                                    disabled={loading}
                                    data-testid="refresh-btn"
                                >
                                    <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
                                </Button>
                            </div>
                        </div>

                        {/* Sélecteur de restaurants avec logos (style Ventes Générales) */}
                        <RestaurantSelector 
                            selected={selectedRestaurant} 
                            onSelect={setSelectedRestaurant} 
                        />

                        {/* Erreur */}
                        {error && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        {/* Totaux globaux */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Card className="bg-primary/5">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Total Couverts</p>
                                            <p className="text-3xl font-bold text-primary">{formatNumber(filteredTotals.total_covers)}</p>
                                        </div>
                                        <Users className="h-8 w-8 text-primary/50" />
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Couverts Midi</p>
                                            <p className="text-2xl font-bold">{formatNumber(filteredTotals.total_covers_midi)}</p>
                                        </div>
                                        <Sun className="h-8 w-8 text-yellow-500/50" />
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Couverts Soir</p>
                                            <p className="text-2xl font-bold">{formatNumber(filteredTotals.total_covers_soir)}</p>
                                        </div>
                                        <Moon className="h-8 w-8 text-indigo-500/50" />
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card className="bg-amber-50 dark:bg-amber-950/20">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Clients VIP</p>
                                            <p className="text-2xl font-bold text-amber-600">{filteredVips.length}</p>
                                        </div>
                                        <Star className="h-8 w-8 text-amber-500/50" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Grille des restaurants */}
                        <div>
                            <h2 className="text-lg font-semibold mb-4">
                                {selectedRestaurant.id === 'all' 
                                    ? 'Réservations par restaurant' 
                                    : `Réservations - ${selectedRestaurant.name}`
                                }
                            </h2>
                            {loading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {[...Array(selectedRestaurant.id === 'all' ? 6 : 1)].map((_, i) => (
                                        <Card key={i} className="animate-pulse">
                                            <CardHeader className="pb-2">
                                                <div className="h-5 bg-muted rounded w-3/4"></div>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-3">
                                                    <div className="h-8 bg-muted rounded"></div>
                                                    <div className="h-4 bg-muted rounded w-1/2"></div>
                                                    <div className="h-4 bg-muted rounded w-1/2"></div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            ) : filteredData.length > 0 ? (
                                <div className={cn(
                                    "grid gap-4",
                                    selectedRestaurant.id === 'all' 
                                        ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" 
                                        : "grid-cols-1 max-w-md"
                                )}>
                                    {filteredData.map((resto) => (
                                        <ReservationCard 
                                            key={resto.restaurant} 
                                            {...resto} 
                                            vips={getVipsForRestaurant(resto.restaurant)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <Card>
                                    <CardContent className="py-10 text-center">
                                        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground">Aucune réservation pour cette date</p>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* Section Groupes BtoB */}
                        {filteredBtob.length > 0 && (
                            <div>
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <Briefcase className="h-5 w-5 text-blue-500" />
                                    Groupes BtoB du jour
                                    <span className="text-sm font-normal text-muted-foreground">
                                        ({filteredBtob.reduce((sum, g) => sum + g.pax, 0)} PAX)
                                    </span>
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredBtob.map((group, index) => (
                                        <Card key={index} className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                                            <CardContent className="pt-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <Briefcase className="h-4 w-4 text-blue-600" />
                                                        <span className="font-semibold">{group.client_name}</span>
                                                    </div>
                                                    <span className="text-lg font-bold text-blue-600">{group.pax} PAX</span>
                                                </div>
                                                <div className="text-sm text-muted-foreground">
                                                    <p>{group.restaurant}</p>
                                                    <p className="flex items-center gap-1">
                                                        {group.shift === 'midi' ? (
                                                            <Sun className="h-3 w-3 text-yellow-500" />
                                                        ) : (
                                                            <Moon className="h-3 w-3 text-indigo-500" />
                                                        )}
                                                        Service {group.shift}
                                                        {group.pax_midi > 0 && group.pax_soir > 0 && (
                                                            <span className="ml-2">({group.pax_midi} midi / {group.pax_soir} soir)</span>
                                                        )}
                                                    </p>
                                                    {group.amount_ht > 0 && (
                                                        <p className="text-blue-600 font-medium mt-1">
                                                            {formatNumber(group.amount_ht)} € HT
                                                        </p>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Section VIP */}
                        <div>
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <Star className="h-5 w-5 text-amber-500" />
                                Clients VIP du jour
                            </h2>
                            {filteredVips.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredVips.map((vip, index) => (
                                        <VipCard key={index} vip={vip} />
                                    ))}
                                </div>
                            ) : (
                                <Card>
                                    <CardContent className="py-8 text-center">
                                        <Star className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-muted-foreground">Aucun client VIP prévu ce jour</p>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Les clients ayant fait 3+ visites ou marqués VIP apparaîtront ici
                                        </p>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
